import React from 'react';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';
import get from 'lodash/get';
import { defineMessages } from 'react-intl';
import { SimpleLoadingComponent } from '../../utils/apollo';
import AMConfigureFrame from '../../components/AMConfigureFrame';
import { withEnabledFeatures } from '../../utils/features';
import { REMOVE_DIVERSITY_FILTERS } from '../Authorization/constants';

export const query = gql`
  query AdvaAwareAMSubscriptions {
    viewer {
      id
      heAccount {
        id
        activeMatchSubscriptions {
          edges {
            node {
              id
              majors
              audience {
                diversity
              }
              messages {
                competitors {
                  scid
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const hasMajors = (edges) => !!edges.find((edge) => edge.node.majors);
export const hasCompetitors = (edges) => !!edges.find((edge) =>
// eslint-disable-next-line lodash/prefer-get
  edge.node.messages?.competitors && edge.node.messages.competitors.length);

export const hasDiversityEnabled = (edges, props) => edges.some((edge) => {
  const enabledFeatures = get(props, 'ownProps.enabledFeatures') || [];
  if (enabledFeatures.includes(REMOVE_DIVERSITY_FILTERS)) return false;
  const { node: { audience } } = edge;
  return audience && audience.diversity === 'RACIAL_AND_ETHNIC_MINORITY';
});

const graphqlData = graphql(query, {
  props: (props) => {
    const edges = get(props, 'data.viewer.heAccount.activeMatchSubscriptions.edges', []);
    return {
      ...props,
      hasMajors: hasMajors(edges),
      hasCompetitors: hasCompetitors(edges),
      hasDiversityEnabled: hasDiversityEnabled(edges, props),
    };
  },
});

export const messages = defineMessages({
  overview: { id: 'am-config.menu.overview', defaultMessage: 'Overview' },
  threshold: { id: 'am-config.menu.threshold', defaultMessage: 'Academic Threshold' },
  diversity: { id: 'am-config.menu.diversity', defaultMessage: 'Diversity' },
  competitors: { id: 'am-config.menu.competitors', defaultMessage: 'Competitors' },
  majors: { id: 'am-config.menu.majors', defaultMessage: 'Majors' },
  understanding: { id: 'am-config.menu.understanding', defaultMessage: 'Understanding Your Subscriptions' },
  configure: { id: 'am-config.menu.configure', defaultMessage: 'Configure Your Audience' },
  compose: { id: 'am-config.menu.compose', defaultMessage: 'Compose Your Messages' },
});

const ownMessages = defineMessages({
  header: {
    id: 'am-config.advaware.header', defaultMessage: 'Advanced Awareness',
  },
  subheader: {
    id: 'am-config.advaware.subheader',
    defaultMessage: 'Ensure the best-fit students are seeing your messages as they research college options.',
  },
});

const OVERVIEW_PATHNAME = '/advanced-awareness/overview';
const THRESHOLD_PATHNAME = '/advanced-awareness/threshold';
const DIVERSITY_PATHNAME = '/advanced-awareness/diversity';
const COMPETITORS_PATHNAME = '/advanced-awareness/competitors';
const MAJORS_PATHNAME = '/advanced-awareness/majors';

const menuConfig = [
  { to: OVERVIEW_PATHNAME, message: messages.overview },
  { to: THRESHOLD_PATHNAME, message: messages.threshold },
  { to: DIVERSITY_PATHNAME, message: messages.diversity },
  { to: COMPETITORS_PATHNAME, message: messages.competitors },
  { to: MAJORS_PATHNAME, message: messages.majors },
];

const secondMenuConfig = (props) => [
  {
    section: messages.overview,
    links: [{ to: OVERVIEW_PATHNAME, message: messages.understanding }],
  }, {
    section: messages.configure,
    links: [
      { to: THRESHOLD_PATHNAME, message: messages.threshold },
      { to: DIVERSITY_PATHNAME, message: messages.diversity },
    ],
  }, {
    section: messages.compose,
    links: [
      { to: COMPETITORS_PATHNAME, message: messages.competitors },
      { to: MAJORS_PATHNAME, message: messages.majors },
    ],
  },
];

const AdvAwareConfigureFrame = (props) => (
  <AMConfigureFrame
    title="Advanced Awareness"
    header={ownMessages.header}
    subheader={ownMessages.subheader}
    menuConfig={menuConfig}
    secondMenuConfig={secondMenuConfig(props)}
    {...props}
  />
);

export default SimpleLoadingComponent(compose(withEnabledFeatures, graphqlData))(AdvAwareConfigureFrame);
